/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Registration deadline helpers for the appointment registration modal.
 * @module logic/appointments/appointmentRegistrationDate
 */

/** Hour component for end-of-day registration cutoff (23:59:00 local). */
export const END_OF_DAY_HOUR = 23;

/** Minute component for end-of-day registration cutoff. */
export const END_OF_DAY_MINUTE = 59;

/** Second component for end-of-day registration cutoff. */
export const END_OF_DAY_SECOND = 0;

/**
 * Builds a local Date at end-of-day for an ISO date string (`YYYY-MM-DD`).
 * @param {string} dateString - Event registration deadline date
 * @returns {Date}
 */
export const buildRegistrationDueDate = (dateString) => {
  const dateParts = dateString.split("-");
  return new Date(
    parseInt(dateParts[0], 10),
    parseInt(dateParts[1], 10) - 1,
    parseInt(dateParts[2], 10),
    END_OF_DAY_HOUR,
    END_OF_DAY_MINUTE,
    END_OF_DAY_SECOND,
  );
};

/**
 * Whether the registration deadline has passed relative to `now`.
 * @param {string} fechaLimiteRegistro - Event registration deadline (`YYYY-MM-DD`)
 * @param {Date} [now=new Date()] - Reference time
 * @returns {boolean}
 */
export const isRegistrationPastDue = (fechaLimiteRegistro, now = new Date()) => {
  if (!fechaLimiteRegistro) {
    return false;
  }
  return now > buildRegistrationDueDate(fechaLimiteRegistro);
};

/**
 * Whether the submit button should be disabled for capacity/role/deadline reasons.
 * Missing required fields no longer disable the button; they are highlighted on submit.
 * @param {Object} options
 * @param {boolean} options.isSubmitting
 * @param {string} options.tipoDeUsuario - Current user type
 * @param {string} options.fechaLimiteRegistro - Event registration deadline
 * @returns {boolean}
 */
export const isRegistrationSubmitDisabled = ({
  isSubmitting,
  tipoDeUsuario,
  fechaLimiteRegistro,
}) =>
  isSubmitting ||
  tipoDeUsuario === "12" ||
  isRegistrationPastDue(fechaLimiteRegistro);
